import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import * as Mui from "@mui/material";
import { useForm } from "react-hook-form";
import { showToast } from "services/utils/Status";
import { useFetch } from "services/hooks/useFetch";
import CloseIcon from "@mui/icons-material/Close";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import sessionHandling from "services/utils/notificationUtils";
import { adminUi } from "../adminListScreenUi";
import {
  designColors,
  designRadii,
  designShell,
  designType,
  APP_BAR_OFFSET,
  panelDrawerSx,
  PANEL_DRAWER_WIDTH,
} from "services/constants/designTokens";
import {
  GlobalFieldLabel as AdminFieldLabel,
  GlobalNoticeBanner as AdminNoticeBanner,
  globalTextFieldSx as adminTextFieldSx,
  useExpandedContentLeft,
  GlobalBreadcrumb,
} from "components/DesignSystem/sharedUiPatterns";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

const EmployeeScreenLink = () => (
  <Mui.Link
    component={RouterLink}
    to="/admin/hrzone/employees"
    underline="hover"
    sx={{ fontWeight: 600, color: designColors.slate800 }}
  >
    People &gt; Employee
  </Mui.Link>
);

// The app shell's own routed-page wrapper (layouts/Admin.js) is the one
// place that actually knows the sidebar's current width — it sets this
// div's marginLeft to 250 or 75 depending on whether the sidebar is open
// or collapsed, a toggle that's local React state we have no way to read
// from here. Rather than hardcode either number (which broke the moment
// the real sidebar was a different width — see git history on this file),
// measure where that div's content actually starts on screen right now.
// Correct at any width, any sidebar state, no guessing.
// Label + tooltip pair used above every input in this panel — kept local
// since no other Administration screen has this exact "editable form field"
// label shape yet (adminUi.fieldLabel is otherwise only used for read-only
// panel values in UserDetailPanel/CompanyDetailPanel).
const FieldLabel = ({ children, tooltip }) => (
  <AdminFieldLabel helperText={tooltip}>{children}</AdminFieldLabel>
);

// The banner explaining this form's scope, used only in the expanded
// full-page layout — the collapsed panel keeps the plainer muted caption it
// already had under the title instead.
const ScopeNotice = () => (
  <AdminNoticeBanner sx={{ mb: 3 }} icon={<PersonAddAlt1Icon fontSize="small" />}>
    This grants system login access only and does not create an employee record.
    To onboard a new employee, use <EmployeeScreenLink /> instead.
  </AdminNoticeBanner>
);

export const CreateUser = ({
  onClose,
  onCreated,
  open = true,
  expanded,
  onToggleExpand,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      user_type: "User",
    },
  });
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const { result: companyDetails } = useFetch(`${domain}get-active-company/`);
  const [buttonLoader, setButtonLoader] = useState(false);
  const contentLeft = useExpandedContentLeft(expanded);

  const handleClose = () => {
    clearErrors();
    onClose?.();
  };

  // Expanded mode is a fixed-position overlay covering the page, but nothing
  // stopped the page underneath from also being scrollable — the overlay's
  // own scroll container plus the still-live background page produced two
  // independent vertical scrollbars at once. Lock the background while
  // expanded, same fix already applied to UserDetailPanel/CompanyDetailPanel.
  useEffect(() => {
    if (!expanded) return;
    if (typeof document === "undefined") return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [expanded]);

  const handleKeyPress = (e) => {
    const charCode = e.charCode;
    if (charCode < 48 || charCode > 57) {
      e.preventDefault();
    }
  };

  const checkSpecialChar = (e) => {
    if (!/^[a-zA-Z0-9 ]*$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const onSubmit = async (data) => {
    setButtonLoader(true);
    try {
      const response = await fetch(`${domain}normal_user_registration/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          phone_number: data.phoneno,
          user_name: data.name,
          email: data.email,
          role: data?.user_type,
          company_id: data.company_id,
        }),
      });
      await response.json();
      if (response.ok) {
        reset();
        showToast(
          "success",
          data?.user_type === "User"
            ? "User created successfully"
            : "Super admin created successfully"
        );
        onCreated?.();
        onClose?.();
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error('REQUEST_FAILED');
      }
    } catch (error) {
      console.error('[Index] operation failed:', error);
      showToast("error", "Couldn't save the user details. Please try again.");
    } finally {
      setButtonLoader(false);
    }
  };

  const handleCompanyName = (event, data) => {
    if (data) {
      setValue("company", data?.label);
      setValue("company_id", data?.id);
    } else {
      setValue("company", "");
      setValue("company_id", null);
    }
  };

  const handleUserType = (event) => {
    setValue("user_type", event.target.value);
  };

  const textFieldSx = adminTextFieldSx;

  // Built once and placed into whichever container layout the current mode
  // needs (a vertical Stack for the collapsed panel, a 2-column Grid for the
  // expanded full page) — the react-hook-form bindings underneath are
  // identical either way, only the surrounding layout differs.
  const nameField = (
    <Mui.Box>
      <FieldLabel tooltip="Enter the full name of the user. This will be used to identify the individual in the system.">
        User Name
      </FieldLabel>
      <Mui.TextField
        {...register("name", {
          required: "Name is required",
          maxLength: {
            value: 30,
            message: "Maximum length is 30 characters",
          },
          minLength: {
            value: 3,
            message: "Minimum length is 3 characters",
          },
          validate: {
            noDigitsOrSpecial: (fieldValue) => {
              if (!/[A-Za-z]/.test(fieldValue)) {
                return "Name must contain at least one alphabet character.";
              }
              if (/^\d+$/.test(fieldValue)) {
                return "Name should not contain only digits.";
              }
              if (!/^[A-Za-z0-9\s&-]+$/.test(fieldValue)) {
                return "Name should only contain alphabets, spaces, -,numbers and & symbol.";
              }
              return true;
            },
            emailAvailable: async (fieldValue) => {},
          },
        })}
        name="name"
        size="small"
        fullWidth
        error={errors.name ? true : false}
        helperText={errors.name ? errors.name.message : null}
        placeholder="Enter the name"
        onKeyPress={checkSpecialChar}
        onBlur={() => trigger("name")}
        sx={textFieldSx}
      />
    </Mui.Box>
  );

  const phoneField = (
    <Mui.Box>
      <FieldLabel tooltip="Input the user’s active mobile number. This may be used for notifications, login authentication, or user verification.">
        Phone Number
      </FieldLabel>
      <Mui.TextField
        {...register("phoneno", {
          required: "Phone number is required",
          minLength: {
            value: 10,
            message: "Minimum length is 10 digits",
          },
          maxLength: {
            value: 15,
            message: "Maximum length is 15 digits",
          },
          validate: {
            employeePhonenumberAvailable: async (fieldValue) => {
              if (fieldValue !== "") {
                const response = await fetch(
                  `${domain}unique/phone-number/?phone_no=${fieldValue}`,
                  {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `token ${token}`,
                    },
                  }
                );
                const resp = await response.json();
                if (resp.exists) {
                  return "Phone number already exists";
                }
              }
            },
          },
        })}
        size="small"
        fullWidth
        onKeyPress={handleKeyPress}
        placeholder="Enter the phone number"
        error={errors.phoneno ? true : false}
        helperText={errors.phoneno ? errors.phoneno.message : null}
        sx={textFieldSx}
      />
    </Mui.Box>
  );

  const companyField = (
    <Mui.Box>
      <FieldLabel tooltip="Select the company the user is associated with from the dropdown list. This links the user to relevant departments, permissions, and data access.">
        Company Name
      </FieldLabel>
      <CustomAutoComplete
        dataList={companyDetails.map((obj) => ({
          ...obj,
          label: obj.companyname,
        }))}
        selectedValue={watch("company")}
        rules={{
          ...register("company", {
            required: "Company name is required",
          }),
        }}
        updateDataFunction={handleCompanyName}
        placeHolder="Select the company name"
        errorMessage={
          errors?.company?.message ? errors?.company?.message : ""
        }
        textBoxError={errors.company ? true : false}
      />
    </Mui.Box>
  );

  const emailField = (
    <Mui.Box>
      <FieldLabel tooltip="Provide a valid email address for the user. The email is essential for communication, system alerts, and possibly login credentials.">
        Email
      </FieldLabel>
      <Mui.TextField
        {...register("email", {
          required: false,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid e-mail address",
          },
          validate: {
            emailAvailable: async (fieldValue) => {
              if (fieldValue !== "") {
                const res = await customGetAPI(
                  `${domain}email_verfication/?email=${fieldValue}`
                );
                if (res?.ok) {
                  if (res?.responseDetails?.exists) {
                    return "Email Already Exists";
                  } else {
                    return true;
                  }
                }
              }
            },
          },
        })}
        error={Boolean(errors.email)}
        helperText={errors.email ? errors.email.message : null}
        placeholder="Enter the email"
        size="small"
        fullWidth
        sx={textFieldSx}
      />
    </Mui.Box>
  );

  const userTypeRadio = (
    <Mui.Radio
      sx={{
        color: "#9CA3AF",
        "&.Mui-checked": {
          color: designColors.tealDark,
        },
        "&:hover": {
          backgroundColor: "rgba(15, 118, 110, 0.08)",
        },
      }}
    />
  );

  const userTypeField = (
    <Mui.Box>
      <FieldLabel tooltip="Choose whether this login should have standard user access or Super Admin access.">
        User Type
      </FieldLabel>
      <Mui.RadioGroup
        row
        onChange={handleUserType}
        aria-labelledby="create-user-type-label"
        defaultValue={watch("user_type")}
        name="radio-buttons-group"
        sx={{
          gap: 2,
          flexWrap: "wrap",
          "& .MuiFormControlLabel-label": {
            fontSize: 14,
            color: designColors.slate800,
          },
        }}
      >
        <Mui.FormControlLabel label="User" value="User" control={userTypeRadio} />
        <Mui.FormControlLabel
          label="Super Admin"
          value="Super Admin"
          control={userTypeRadio}
        />
      </Mui.RadioGroup>
    </Mui.Box>
  );

  const formActions = (
    <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
      <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
      <PrimaryButton
        type="submit"
        loading={buttonLoader}
        disabled={buttonLoader}
      >
        Create
      </PrimaryButton>
    </Mui.Stack>
  );

  // Rendered as a portal straight into document.body — same as MUI's own
  // Drawer/Modal — rather than inline where this component happens to sit
  // in the Users page's tree. That tree is otherwise unchanged between
  // collapsed and expanded (the Drawer just closes; this renders as an
  // *additional*, independent overlay), so nothing about "expanding" can
  // ever perturb the app shell's own layout (sidebar/top bar) the way
  // swapping the page's own returned JSX previously did.
  //
  // Deliberately spans the FULL viewport width (left:0) rather than
  // stopping short of the sidebar at some guessed pixel width — the
  // sidebar collapses to a much narrower icon rail on smaller screens, and
  // a hardcoded offset left a gap there where the list view underneath
  // showed through. Instead this sits at a z-index below the app shell's
  // own fixed sidebar/AppBar (MUI's default theme.zIndex.drawer/appBar are
  // 1200/1100), so whichever of those is actually on screen — at whatever
  // width it currently is — naturally paints over this overlay wherever
  // they overlap. No pixel-width bookkeeping needed, correct at any width.
  if (expanded) {
    return createPortal(
      <Mui.Box
        sx={{
          position: "fixed",
          // Starts exactly below the app shell's fixed AppBar instead of
          // guessing at padding to clear it — APP_BAR_OFFSET is this app's
          // own constant for "tuck directly under the fixed AppBar" (see
          // designTokens.js), the same one the sticky table headers use.
          top: APP_BAR_OFFSET,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
        }}
      >
        {/* pl clears the real sidebar (measured above) so content starts
            right where the app's own page content always starts — not
            centered against the full viewport, which counted the width
            hidden behind the sidebar and left an uneven gap. */}
        <Mui.Box
          sx={{
            pl: `${contentLeft}px`,
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* One seamless panel (no border/shadow, just white-on-pageBg
              contrast) — matches UserDetailPanel's expanded layout exactly:
              breadcrumb+title header, a single scrollable fields region, and
              a footer pinned to the bottom, instead of two separate bordered
              cards with a gap between them. */}
          <Mui.Box sx={{ height: "100%", p: 2, boxSizing: "border-box" }}>
            <Mui.Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                boxSizing: "border-box",
                backgroundColor: designColors.white,
                borderRadius: designRadii.card,
              }}
            >
              <Mui.Box sx={{ p: 3, pb: 0 }}>
                <GlobalBreadcrumb
                  trail={[
                    "Administration",
                    { label: "Users", onClick: handleClose },
                    "Create User",
                  ]}
                />
                <Mui.Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}
                  sx={{
                    pb: 2.25,
                    borderBottom: `1px solid ${designColors.slate200}`,
                  }}
                >
                  <Mui.Box>
                    <Mui.Typography sx={designType.pageTitle}>
                      Create User
                    </Mui.Typography>
                    <Mui.Typography sx={designType.pageSubtitle}>
                      Create a new login for someone on your team.
                    </Mui.Typography>
                  </Mui.Box>
                  <Mui.Stack direction="row" spacing={1} alignItems="center">
                    <PanelHeaderIconButton
                      expanded
                      title="Collapse"
                      onClick={onToggleExpand}
                    >
                      <CloseFullscreenIcon />
                    </PanelHeaderIconButton>
                    <PanelHeaderIconButton
                      expanded
                      title="Close"
                      onClick={handleClose}
                    >
                      <CloseIcon />
                    </PanelHeaderIconButton>
                  </Mui.Stack>
                </Mui.Stack>
              </Mui.Box>

              <Mui.Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Mui.Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    px: 3,
                    pt: 2,
                    pb: 2,
                  }}
                >
                  <ScopeNotice />
                  <Mui.Grid container spacing={3}>
                    <Mui.Grid item xs={12}>
                      {nameField}
                    </Mui.Grid>
                    <Mui.Grid item xs={12}>
                      {phoneField}
                    </Mui.Grid>
                    <Mui.Grid item xs={12}>
                      {companyField}
                    </Mui.Grid>
                    <Mui.Grid item xs={12}>
                      {emailField}
                    </Mui.Grid>
                    <Mui.Grid item xs={12}>
                      {userTypeField}
                    </Mui.Grid>
                  </Mui.Grid>
                </Mui.Box>
                <Mui.Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderTop: "1px solid #E2E8F0",
                    backgroundColor: designColors.white,
                  }}
                >
                  <Mui.Stack
                    direction="row"
                    spacing={1.25}
                    justifyContent="flex-end"
                  >
                    <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
                    <PrimaryButton
                      type="submit"
                      loading={buttonLoader}
                      disabled={buttonLoader}
                    >
                      Create User
                    </PrimaryButton>
                  </Mui.Stack>
                </Mui.Box>
              </Mui.Box>
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>,
      document.body
    );
  }

  // Rendered as this component's own Drawer (rather than the parent
  // wrapping it externally) so the parent can mount CreateUser once and
  // just flip `open`/`expanded` props — switching between this collapsed
  // Drawer and the expanded portal above no longer unmounts/remounts the
  // component, which used to wipe out whatever the user had typed so far.
  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={panelDrawerSx()}
    >
    <Mui.Box
      sx={{
        width: PANEL_DRAWER_WIDTH,
        maxWidth: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxSizing: "border-box",
      }}
      role="presentation"
    >
      <Mui.Box sx={{ p: 3, pb: 0 }}>
      <Mui.Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Mui.Box sx={{ pr: 2 }}>
          <Mui.Typography sx={adminUi.panelTitle}>Create User</Mui.Typography>
          <Mui.Typography sx={{ ...adminUi.panelMutedText, mt: 0.5 }}>
            Create a new login for someone on your team.
          </Mui.Typography>
        </Mui.Box>
        <Mui.Stack direction="row" spacing={1}>
          <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
            <Mui.IconButton
              onClick={onToggleExpand}
              size="small"
              sx={{ ...designShell.panelSoftIconButton }}
            >
              <OpenFullViewIcon fontSize="small" />
            </Mui.IconButton>
          </Mui.Tooltip>
          <Mui.IconButton
            onClick={handleClose}
            size="small"
            sx={{
              ...designShell.panelSoftIconButton,
              color: designColors.slate500,
            }}
          >
            <CloseIcon />
          </Mui.IconButton>
        </Mui.Stack>
      </Mui.Stack>

      <Mui.Box
        sx={{ mt: 2, borderBottom: `1px solid ${designColors.slate200}` }}
      />
      </Mui.Box>

      <Mui.Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Mui.Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            pt: 2.5,
            px: 3,
            pb: 2,
          }}
        >
          <Mui.Box
            sx={{
              mb: 2.5,
              p: 1.5,
              borderRadius: designRadii.banner,
              backgroundColor: designColors.tealTint,
              border: "1px solid #B7E4DB",
            }}
          >
            <Mui.Typography
              sx={{
                fontSize: 13,
                color: designColors.slate700,
                lineHeight: 1.55,
              }}
            >
              This grants system login access only and does not create an
              employee record. To onboard a new employee, use
              {" "}
              <EmployeeScreenLink />
              {" "}
              instead.
            </Mui.Typography>
          </Mui.Box>
          <Mui.Stack spacing={2.5}>
            {nameField}
            {phoneField}
            {companyField}
            {emailField}
            {userTypeField}
          </Mui.Stack>
        </Mui.Box>
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: designColors.white,
          }}
        >
          {formActions}
        </Mui.Box>
      </Mui.Box>
    </Mui.Box>
    </Mui.Drawer>
  );
};
